/**
 * What the signed-in person may do, read off the profile the shell already holds.
 *
 * The codenames are the backend's — `add__member`, `change__club` — and a
 * superuser passes every check, as they do there. The answer is only ever a
 * hint for what to show: the API refuses on its own, so a button hidden here
 * is a button that would have failed, not a rule enforced here.
 *
 * `useCan` answers false until the profile has loaded, so an action never
 * flashes into view for a person who cannot take it.
 */

import { useQuery } from "@tanstack/react-query";

import { profileQuery } from "@/services/queries";
import type { Profile } from "@/meta/types";

/** Any one of the codenames is enough — the same rule as `requires(..., require_all=False)`. */
export function can(profile: Profile | undefined, ...codenames: string[]): boolean {
  if (!profile) return false;
  if (profile.is_superuser) return true;
  // Read defensively rather than trusted. This is a hint about what to show,
  // and the answer for a profile that is not the shape it should be is "no" —
  // not a TypeError that takes the whole screen down. `/me` answering with
  // something unexpected, for a moment or for good, must cost a button rather
  // than the page the button is on.
  return codenames.some((codename) => profile.permissions?.includes(codename) ?? false);
}

export function useCan(): (...codenames: string[]) => boolean {
  const { data: profile } = useQuery(profileQuery());
  return (...codenames) => can(profile, ...codenames);
}

/**
 * Whether this person has been marked as hiding one of these screens.
 *
 * The one question in the app that is not "may they", and so the one that
 * cannot go through `can`. `can` says yes to a superuser for every codename
 * that exists, `hide__*` included — asked that way, the account with full
 * access is refused every page in the product, which is exactly what happened
 * the first time this was wired up.
 *
 * A superuser is never hidden from anything, full stop. Full access is full
 * access: there is nothing further to check, and a `hide__*` that reached
 * their profile through some group is not a reason to take a screen away from
 * the one person meant to reach all of them.
 *
 * For everybody else it is a literal read of the list — no bypass, no
 * inference. Any one of the codenames is enough, as everywhere else, and the
 * answer for a profile that is missing or malformed is "not hidden": that
 * costs a visible row rather than an empty product.
 */
export function hiddenFrom(profile: Profile | undefined, ...codenames: string[]): boolean {
  if (!profile || profile.is_superuser) return false;
  return codenames.some((codename) => profile.permissions?.includes(codename) ?? false);
}

/**
 * The four built-in ranks, lowest first, and the group each is held through.
 *
 * The backend's `ROLE_OF_GROUP` and `RANKED`, read the same way: a superuser is
 * the top rank whatever their groups say, and anybody holding none of the four
 * is an Associate — a rank nobody recognised is not a reason to hand out more
 * than the floor. A profile that has not loaded is read as an Associate too,
 * for the same reason `useCan` answers false until it has: the cautious answer
 * costs a control for a moment, the other one shows a control that is not
 * theirs.
 */
export type Rank = "associate" | "supervisor" | "manager" | "super_user";

const RANK_OF_GROUP: Record<string, Rank> = {
  Associate: "associate",
  Supervisor: "supervisor",
  "Manager / Corporate Admin": "manager",
  "Super User": "super_user",
};
const RANKED: Rank[] = ["associate", "supervisor", "manager", "super_user"];

export function rankOf(profile: Profile | undefined): Rank {
  if (!profile) return "associate";
  if (profile.is_superuser) return "super_user";
  const held = (profile.groups ?? [])
    .map((group) => RANK_OF_GROUP[group.name])
    .filter((rank): rank is Rank => rank !== undefined);
  return held.reduce(
    (highest, rank) => (RANKED.indexOf(rank) > RANKED.indexOf(highest) ? rank : highest),
    "associate" as Rank,
  );
}

/** Whether this rank is the one named or higher. */
export const atLeast = (rank: Rank, floor: Rank) =>
  RANKED.indexOf(rank) >= RANKED.indexOf(floor);

/** `rankOf`, bound to the signed-in profile. */
export function useRank(): Rank {
  const { data: profile } = useQuery(profileQuery());
  return rankOf(profile);
}

/** `hiddenFrom`, bound to the signed-in profile. */
export function useHiddenFrom(): (...codenames: string[]) => boolean {
  const { data: profile } = useQuery(profileQuery());
  return (...codenames) => hiddenFrom(profile, ...codenames);
}
